import type { SavedWord } from "./worksheet-sources.js";

/**
 * A reader's saved words, narrowed to a tag and cut into parts a sheet holds.
 *
 * A published List too long for one sheet is split into parts by the build,
 * and the List select offers the parts instead. Saved words are the reader's
 * and arrive in the browser, so they are split here, the same way: in the
 * order the export gives them, fifty words to a part, as the HSK bands are.
 * Words that fit on one sheet are one part.
 */

/**
 * The most words, or distinct 汉字, one sheet takes.
 *
 * The same figure as `sheetLimit` in `src/content/lists/list-records.ts`,
 * which decides which published Lists the select offers whole.
 */
export const printLimit = 300;

/** How many words a part holds, once the words are split. */
export const partSize = 50;

/** A tag, and how many of the reader's words carry it. */
export interface SavedTag {
  readonly tag: string;
  readonly count: number;
}

/** Every tag on the words, in alphabetical order, with how many carry each. */
export function savedTags(words: readonly SavedWord[]): readonly SavedTag[] {
  const counts = new Map<string, number>();
  for (const saved of words) {
    for (const tag of saved.tags) {
      counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
  }

  return [...counts]
    .map(([tag, count]) => ({ tag, count }))
    .sort((one, other) => one.tag.localeCompare(other.tag));
}

/** The words carrying a tag, or every word for no tag. */
export function taggedWords(
  words: readonly SavedWord[],
  tag: string,
  band = "",
): readonly string[] {
  return words
    .filter(
      (saved) =>
        (tag === "" || saved.tags.includes(tag)) &&
        (band === "" || saved.band === band),
    )
    .map((saved) => saved.word);
}

/** Whether the words fit on one sheet, by the rule a published List keeps. */
export function printsWhole(words: readonly string[]): boolean {
  const characters = new Set(
    words.flatMap((word) =>
      Array.from(word).filter((letter) => /\p{Script=Han}/u.test(letter)),
    ),
  );

  return words.length <= printLimit && characters.size <= printLimit;
}

/** The words cut into the parts a sheet is printed from. */
export function savedParts(
  words: readonly string[],
): readonly (readonly string[])[] {
  if (printsWhole(words)) {
    return [words];
  }
  const parts: string[][] = [];
  for (let at = 0; at < words.length; at += partSize) {
    parts.push(words.slice(at, at + partSize));
  }

  return parts;
}
